/**
 * WorkModePage — Governed Action Layer UI
 *
 * Core execution interface:
 * 1. Natural-language objective input
 * 2. Task decomposition preview (risk badges, write markers, connector status)
 * 3. Autonomy selector with trust gating
 * 4. Live execution timeline with idempotency, drafts, artifact links
 * 5. Outcome verification (accept all / reject all / partial per-step)
 * 6. Relief dashboard (time saved, trust score)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Target, Play, Shield, CheckCircle, XCircle, Zap, Loader2,
    Clock, Eye, X, RefreshCw, Check,
    AlertTriangle, Lock, Unlock, Send, FileText,
    Undo2, PauseCircle, Download, ChevronDown, ChevronRight,
    TrendingUp, Timer, Award, Activity, Cpu, Search,
    Globe, Ban, Edit3, Layers, Phone, Link2, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { api } from '../lib/api';
import { getApiUrl } from '../lib/env';
import { replicaService } from '../lib/services';

// ============================================================================
// Types
// ============================================================================

interface TaskNode {
    id: string;
    action: string;
    tool: string;
    params: Record<string, any>;
    riskClass: 'A' | 'B' | 'C';
    isWriteAction: boolean;
    isReversible: boolean;
    estimatedTimeSaved: number;
    requiresApproval: boolean;
    connectorStatus: 'READY' | 'MISSING' | 'UNHEALTHY';
    batchGroupId?: string;
}

interface BatchGroup {
    id: string;
    label: string;
    actionType: string;
    itemCount: number;
    taskNodeIds: string[];
}

interface PlanPreview {
    actionRequestId: string;
    taskTree: TaskNode[];
    batchGroups: BatchGroup[];
    missingConnectors: string[];
    estimatedTimeSaved: number;
    totalSteps: number;
    confidence: number;
    effectiveMode: string;
    modeDegraded: boolean;
    modeReason?: string;
}

interface StepSummary {
    stepIndex: number;
    action: string;
    status: string;
    isReversible: boolean;
    artifactId: string | null;
    stepVerification: string;
}

interface VerificationSummary {
    actionRequestId: string;
    objective: string;
    totalSteps: number;
    completedSteps: number;
    draftsPending: number;
    failedSteps: number;
    steps: StepSummary[];
    totalTimeSaved: number;
    artifacts: { id: string; title: string }[];
}

interface ReliefSummary {
    totalTimeSavedSec: number;
    totalTimeSavedFormatted: string;
    totalActionsCompleted: number;
    totalActionsBlocked: number;
    totalEscalations: number;
    noEscalationStreak: number;
    avgConfidence: number;
}

interface TrustProfile {
    score: number;
    currentMaxMode: string;
    nextThreshold: number;
    actionsToNextLevel: number;
    hasVerifiedGuardian: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const RISK_STYLES: Record<string, { label: string; bg: string; text: string; border: string }> = {
    A: { label: 'Safe', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    B: { label: 'Moderate', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
    C: { label: 'Critical', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};

const MODE_INFO: Record<string, { label: string; icon: React.ElementType; description: string; color: string }> = {
    MANUAL: { label: 'Manual', icon: Eye, description: 'Review every step before execution', color: 'text-slate-400' },
    SUPERVISED: { label: 'Supervised', icon: Shield, description: 'Auto-execute reads, review writes', color: 'text-hig-accent' },
    SEMI_AUTO: { label: 'Semi-Auto', icon: Zap, description: 'Auto-execute safe writes, review critical', color: 'text-amber-400' },
    AUTONOMOUS: { label: 'Autonomous', icon: Cpu, description: 'Full autonomy — Guardian oversight only', color: 'text-purple-400' },
};

const STEP_STATUS_STYLES: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
    SUCCESS: { label: 'Completed', color: 'text-emerald-400', Icon: CheckCircle },
    FAILED: { label: 'Failed', color: 'text-red-400', Icon: XCircle },
    BLOCKED: { label: 'Blocked', color: 'text-amber-400', Icon: Shield },
    DRAFT_CREATED: { label: 'Draft Created', color: 'text-hig-accent', Icon: Edit3 },
    DRY_RUN: { label: 'Dry Run', color: 'text-slate-400', Icon: Eye },
    SKIPPED: { label: 'Skipped', color: 'text-slate-500', Icon: Ban },
};

const TOOL_ICONS: Record<string, React.ElementType> = {
    gmail: Send,
    calendar: Clock,
    llm: Cpu,
    sms: Phone,
    webhook: Globe,
    notes: FileText,
    document: FileText,
};

function formatTime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

// ============================================================================
// Props
// ============================================================================

interface WorkModePageProps {
    onNavigate: (view: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function WorkModePage({ onNavigate }: WorkModePageProps) {
    const { user } = useAuth();
    const { userReplicas: contextReplicas } = useReplica();

    // Replica selection
    const [backendReplicas, setBackendReplicas] = useState<{ id: string; name: string }[]>([]);
    const [selectedReplicaId, setSelectedReplicaId] = useState('');

    // Objective input
    const [objective, setObjective] = useState('');
    const [isPlanning, setIsPlanning] = useState(false);

    // Plan preview
    const [preview, setPreview] = useState<PlanPreview | null>(null);
    const [autonomyMode, setAutonomyMode] = useState<string>('SUPERVISED');
    const [writeApproved, setWriteApproved] = useState(false);
    const [dryRun, setDryRun] = useState(false);

    // Execution state
    const [phase, setPhase] = useState<'input' | 'preview' | 'executing' | 'verify'>('input');
    const [isExecuting, setIsExecuting] = useState(false);

    // Verification
    const [verification, setVerification] = useState<VerificationSummary | null>(null);
    const [stepActions, setStepActions] = useState<Record<number, string>>({});

    // Relief + Trust
    const [relief, setRelief] = useState<ReliefSummary | null>(null);
    const [trustProfile, setTrustProfile] = useState<TrustProfile | null>(null);

    // Connected tools
    const [connectedTools, setConnectedTools] = useState<{ toolId: string; name: string; status: string }[]>([]);

    // ─── Data Loading ──────────────────────────────────────

    useEffect(() => {
        if (!user?.uid) return;
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas && res.data.replicas.length > 0) {
                setBackendReplicas(res.data.replicas.map((r: any) => ({ id: r.id, name: r.name || 'Unnamed' })));
            }
        }).catch(() => { });
    }, [user?.uid]);

    const replicas = (() => {
        const contextMapped = (contextReplicas || []).map((r: any) => ({ id: r.id, name: r.name || 'Unnamed' }));
        const backendIds = new Set(backendReplicas.map(r => r.id));
        return [...backendReplicas, ...contextMapped.filter((r: any) => !backendIds.has(r.id))];
    })();

    useEffect(() => {
        if (!selectedReplicaId && replicas.length > 0) setSelectedReplicaId(replicas[0].id);
    }, [replicas, selectedReplicaId]);

    // Load relief + trust + connected tools on mount
    useEffect(() => {
        if (!user?.uid) return;
        api.get<ReliefSummary>('/relief').then(res => { if (res.data) setRelief(res.data); }).catch(() => { });
        api.get<TrustProfile>('/relief/trust').then(res => { if (res.data) setTrustProfile(res.data); }).catch(() => { });
        api.get<any>('/mcp/catalog').then(res => {
            if (res.data?.connectors) {
                setConnectedTools(res.data.connectors.map((c: any) => ({
                    toolId: c.toolId,
                    name: c.name || c.toolId,
                    status: c.status || 'disconnected',
                })));
            }
        }).catch(() => { });
    }, [user?.uid]);

    // ─── Actions ───────────────────────────────────────────

    const submitObjective = useCallback(async () => {
        if (!objective.trim() || !selectedReplicaId || !user?.uid) return;
        setIsPlanning(true);
        try {
            const res = await api.post<PlanPreview>('/actions/plan', {
                userId: user.uid,
                replicaId: selectedReplicaId,
                objective: objective.trim(),
                autonomyMode,
                dryRun,
            });
            if (res.data) {
                setPreview(res.data);
                setPhase('preview');
            }
        } catch (err: any) {
            console.error('Planning failed:', err);
        } finally {
            setIsPlanning(false);
        }
    }, [objective, selectedReplicaId, user?.uid, autonomyMode, dryRun]);

    const approveAndExecute = useCallback(async () => {
        if (!preview) return;
        setIsExecuting(true);
        setPhase('executing');
        try {
            await api.post(`/actions/${preview.actionRequestId}/approve`, { writeApproved });
            // Load verification
            const res = await api.get<VerificationSummary>(`/actions/${preview.actionRequestId}/verify`);
            if (res.data) {
                setVerification(res.data);
                setPhase('verify');
            }
        } catch (err: any) {
            console.error('Execution failed:', err);
        } finally {
            setIsExecuting(false);
        }
    }, [preview, writeApproved]);

    const cancelExecution = useCallback(async () => {
        if (!preview) return;
        try {
            await api.post(`/actions/${preview.actionRequestId}/cancel`);
            setPhase('input');
            setPreview(null);
        } catch (err: any) {
            console.error('Cancel failed:', err);
        }
    }, [preview]);

    const processVerification = useCallback(async (action: string) => {
        if (!preview) return;
        try {
            const stepActionEntries = Object.entries(stepActions).map(([idx, a]) => ({
                stepIndex: parseInt(idx),
                action: a,
            }));
            await api.post(`/actions/${preview.actionRequestId}/verify`, {
                action,
                stepActions: action === 'PARTIAL' ? stepActionEntries : undefined,
            });
            // Refresh relief
            api.get<ReliefSummary>('/relief').then(res => { if (res.data) setRelief(res.data); }).catch(() => { });
            api.get<TrustProfile>('/relief/trust').then(res => { if (res.data) setTrustProfile(res.data); }).catch(() => { });
            // Reset
            setPhase('input');
            setPreview(null);
            setVerification(null);
            setObjective('');
        } catch (err: any) {
            console.error('Verification failed:', err);
        }
    }, [preview, stepActions]);

    const rerunRejected = useCallback(async () => {
        if (!preview) return;
        try {
            const res = await api.post<{ newActionRequestId: string }>(`/actions/${preview.actionRequestId}/rerun`);
            if (res.data) {
                // TODO: Load new preview for the rerun request
            }
        } catch (err: any) {
            console.error('Rerun failed:', err);
        }
    }, [preview]);

    const resetAll = () => {
        setPhase('input');
        setPreview(null);
        setVerification(null);
        setObjective('');
        setWriteApproved(false);
        setStepActions({});
    };

    // ─── Render ────────────────────────────────────────────

    return (
        <div className="max-w-5xl mx-auto animate-fade-in pb-8">
            {/* ── Header ── */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                            <Target size={20} className="text-purple-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary">Work Mode</h1>
                            <p className="text-xs text-hig-text-tertiary uppercase tracking-widest font-bold">Governed Action Layer</p>
                        </div>
                    </div>
                    <p className="text-sm text-hig-text-secondary mt-2">
                        Describe what you need — your Replica will plan, preview, and execute with full governance
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onNavigate('work')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all"
                    >
                        <Layers size={14} /> Mandates
                    </button>
                    <button
                        onClick={() => onNavigate('daily-routines')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all"
                    >
                        <Clock size={14} /> Daily Routines
                    </button>
                    {phase !== 'input' && (
                        <button
                            onClick={resetAll}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all"
                        >
                            <RefreshCw size={14} /> New
                        </button>
                    )}
                </div>
            </div>

            {/* ── Relief Dashboard (always visible) ── */}
            {relief && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="glass p-3 text-center">
                        <Timer size={16} className="text-hig-accent mx-auto mb-1" />
                        <div className="text-lg font-bold text-hig-text-primary">{relief.totalTimeSavedFormatted}</div>
                        <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider">Time Saved</div>
                    </div>
                    <div className="glass p-3 text-center">
                        <Activity size={16} className="text-emerald-400 mx-auto mb-1" />
                        <div className="text-lg font-bold text-hig-text-primary">{relief.totalActionsCompleted}</div>
                        <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider">Actions Done</div>
                    </div>
                    <div className="glass p-3 text-center">
                        <TrendingUp size={16} className="text-purple-400 mx-auto mb-1" />
                        <div className="text-lg font-bold text-hig-text-primary">{relief.noEscalationStreak}</div>
                        <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider">Clean Streak</div>
                    </div>
                    {trustProfile && (
                        <div className="glass p-3 text-center">
                            <Award size={16} className="text-amber-400 mx-auto mb-1" />
                            <div className="text-lg font-bold text-hig-text-primary">{trustProfile.score}</div>
                            <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider">Trust Score</div>
                            <div className="w-full bg-hig-bg-tertiary rounded-full h-1 mt-1.5">
                                <div
                                    className="bg-gradient-to-r from-amber-500 to-purple-500 h-1 rounded-full transition-all"
                                    style={{ width: `${Math.min(100, (trustProfile.score / trustProfile.nextThreshold) * 100)}%` }}
                                />
                            </div>
                            <div className="text-[9px] text-hig-text-tertiary mt-0.5">
                                {trustProfile.actionsToNextLevel > 0
                                    ? `${trustProfile.actionsToNextLevel} to next level`
                                    : 'Max mode unlocked'}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Connected Tools ── */}
            <div className="glass p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-hig-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                        <Link2 size={12} /> Connected Tools
                    </h3>
                    <button
                        onClick={() => onNavigate('integrations')}
                        className="text-[10px] text-purple-400 hover:text-purple-300 font-medium"
                    >
                        Manage Connections →
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {[
                        { id: 'gmail', name: 'Gmail', icon: Send },
                        { id: 'calendar', name: 'Calendar', icon: Clock },
                        { id: 'sms', name: 'Phone/SMS', icon: Phone },
                        { id: 'document', name: 'Documents', icon: FileText, alwaysConnected: true },
                    ].map(tool => {
                        const Icon = tool.icon;
                        const connected = (tool as any).alwaysConnected || connectedTools.some(c => c.toolId === tool.id && c.status === 'connected');
                        return (
                            <div
                                key={tool.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${connected
                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                    : 'bg-hig-bg-tertiary text-hig-text-tertiary border border-hig-border'
                                    }`}
                            >
                                <Icon size={12} />
                                {tool.name}
                                {connected ? (
                                    <CheckCircle size={10} />
                                ) : (
                                    <button
                                        onClick={() => onNavigate('integrations')}
                                        className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                                    >
                                        Connect
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Replica Selector ── */}
            <div className="glass p-4 mb-6">
                <div className="flex items-center gap-4">
                    <label className="text-xs font-medium text-hig-text-secondary uppercase tracking-wide whitespace-nowrap">Active Replica</label>
                    {replicas.length > 0 ? (
                        <div className="flex items-center gap-2 overflow-x-auto">
                            {replicas.map((r: any) => (
                                <button
                                    key={r.id}
                                    onClick={() => setSelectedReplicaId(r.id)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${selectedReplicaId === r.id
                                        ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                        : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'
                                        }`}
                                >
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold">
                                        {r.name?.[0] || '?'}
                                    </div>
                                    {r.name}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-hig-text-tertiary">No replicas yet</span>
                            <button onClick={() => onNavigate('create-replica')} className="text-sm text-purple-400 hover:underline">
                                Create one →
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ═══════════════════════════════════════════ */}
            {/* PHASE: INPUT                                */}
            {/* ═══════════════════════════════════════════ */}

            {phase === 'input' && (
                <div className="space-y-4">
                    {/* Objective Input */}
                    <div className="glass p-6">
                        <label className="text-sm font-medium text-hig-text-primary mb-3 block">
                            What do you need your Replica to do?
                        </label>
                        <textarea
                            value={objective}
                            onChange={(e) => setObjective(e.target.value)}
                            placeholder="e.g., Summarize my unread emails from today and draft replies to anything urgent..."
                            className="w-full bg-hig-bg-tertiary border border-hig-border rounded-xl p-4 text-sm text-hig-text-primary placeholder-hig-text-tertiary resize-none focus:outline-none focus:border-purple-500/40 transition-all"
                            rows={3}
                        />
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-3">
                                {/* Autonomy mode selector */}
                                <div className="flex items-center gap-1 bg-hig-bg-tertiary rounded-lg p-0.5">
                                    {Object.entries(MODE_INFO).map(([mode, info]) => {
                                        const ModeIcon = info.icon;
                                        const isLocked = trustProfile && mode !== 'MANUAL' && mode !== 'SUPERVISED' &&
                                            ((mode === 'SEMI_AUTO' && (trustProfile?.score ?? 0) < 25) ||
                                                (mode === 'AUTONOMOUS' && ((trustProfile?.score ?? 0) < 50 || !trustProfile?.hasVerifiedGuardian)));
                                        return (
                                            <button
                                                key={mode}
                                                onClick={() => !isLocked && setAutonomyMode(mode)}
                                                disabled={!!isLocked}
                                                title={isLocked ? `Trust score too low (need ${mode === 'SEMI_AUTO' ? 25 : 50})` : info.description}
                                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${autonomyMode === mode
                                                    ? `${info.color} bg-hig-bg-primary border border-current/20`
                                                    : isLocked
                                                        ? 'text-hig-text-tertiary/40 cursor-not-allowed'
                                                        : 'text-hig-text-tertiary hover:text-hig-text-secondary'
                                                    }`}
                                            >
                                                {isLocked ? <Lock size={10} /> : <ModeIcon size={10} />}
                                                {info.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Dry run toggle */}
                                <button
                                    onClick={() => setDryRun(!dryRun)}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${dryRun
                                        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                        : 'bg-hig-bg-tertiary text-hig-text-tertiary hover:text-hig-text-secondary'
                                        }`}
                                >
                                    <Eye size={10} /> Dry Run
                                </button>
                            </div>

                            <button
                                onClick={submitObjective}
                                disabled={!objective.trim() || !selectedReplicaId || isPlanning}
                                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
                            >
                                {isPlanning ? (
                                    <><Loader2 size={14} className="animate-spin" /> Planning...</>
                                ) : (
                                    <><Target size={14} /> Plan & Preview</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════ */}
            {/* PHASE: PREVIEW                              */}
            {/* ═══════════════════════════════════════════ */}

            {phase === 'preview' && preview && (
                <div className="space-y-4">
                    {/* Objective echo */}
                    <div className="glass p-4">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                                <Target size={16} className="text-purple-400" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-hig-text-primary font-medium">{objective}</p>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-[10px] bg-hig-accent/10 text-hig-accent px-1.5 py-0.5 rounded-full">
                                        {preview.totalSteps} steps
                                    </span>
                                    <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded-full">
                                        ~{formatTime(preview.estimatedTimeSaved)} saved
                                    </span>
                                    <span className="text-[10px] bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded-full">
                                        {Math.round(preview.confidence * 100)}% confidence
                                    </span>
                                    {preview.modeDegraded && (
                                        <span className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                            <AlertTriangle size={8} /> Degraded to {preview.effectiveMode}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Missing connectors warning */}
                    {preview.missingConnectors.length > 0 && (
                        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-red-400">Missing Connectors</p>
                                    <p className="text-xs text-red-400/70 mt-1">
                                        The following integrations are required but not connected:
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {preview.missingConnectors.map(c => (
                                            <button
                                                key={c}
                                                onClick={() => onNavigate('integrations')}
                                                className="text-[11px] px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 capitalize"
                                            >
                                                {c} → Connect
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Task Decomposition */}
                    <div className="glass overflow-hidden">
                        <div className="p-4 border-b border-hig-border">
                            <h3 className="text-sm font-semibold text-hig-text-primary flex items-center gap-2">
                                <Layers size={14} className="text-purple-400" />
                                Task Decomposition
                            </h3>
                        </div>

                        {/* Batch groups */}
                        {preview.batchGroups.length > 0 && (
                            <div className="px-4 pt-3">
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {preview.batchGroups.map(bg => (
                                        <span key={bg.id} className="text-[10px] px-2 py-1 rounded-lg bg-hig-accent/10 text-hig-accent border border-hig-accent/20">
                                            ⚡ {bg.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="p-4 pt-2 space-y-2">
                            {preview.taskTree.map((node, idx) => {
                                const risk = RISK_STYLES[node.riskClass];
                                const ToolIcon = TOOL_ICONS[node.tool] || Zap;
                                return (
                                    <div key={node.id} className="flex items-center gap-3 p-3 rounded-lg bg-hig-bg-tertiary/50 hover:bg-hig-bg-tertiary transition-all">
                                        {/* Step number */}
                                        <div className="w-6 h-6 rounded-full bg-hig-bg-secondary flex items-center justify-center text-[10px] text-hig-text-tertiary font-medium flex-shrink-0">
                                            {idx + 1}
                                        </div>

                                        {/* Tool icon */}
                                        <div className={`w-8 h-8 rounded-lg ${risk.bg} flex items-center justify-center flex-shrink-0`}>
                                            <ToolIcon size={14} className={risk.text} />
                                        </div>

                                        {/* Action info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-hig-text-primary">{node.action}</p>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                {/* Risk badge */}
                                                <span className={`text-[9px] px-1 py-0.5 rounded ${risk.bg} ${risk.text} border ${risk.border}`}>
                                                    {risk.label}
                                                </span>
                                                {/* Write badge */}
                                                {node.isWriteAction && (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-0.5">
                                                        <Edit3 size={7} /> Write
                                                    </span>
                                                )}
                                                {/* Irreversible badge */}
                                                {node.isWriteAction && !node.isReversible && (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                                                        Draft-First
                                                    </span>
                                                )}
                                                {/* Connector status */}
                                                {node.connectorStatus !== 'READY' && (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/10 text-red-400">
                                                        {node.connectorStatus}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Time saved estimate */}
                                        <span className="text-[10px] text-hig-text-tertiary flex items-center gap-0.5 flex-shrink-0">
                                            <Timer size={10} /> {formatTime(node.estimatedTimeSaved)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Approval Panel */}
                    <div className="glass p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {/* Write approval toggle */}
                                {preview.taskTree.some(n => n.isWriteAction) && (
                                    <button
                                        onClick={() => setWriteApproved(!writeApproved)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${writeApproved
                                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                            : 'bg-hig-bg-tertiary text-hig-text-secondary'
                                            }`}
                                    >
                                        {writeApproved ? <Unlock size={12} /> : <Lock size={12} />}
                                        {writeApproved ? 'Write Actions Approved' : 'Write Actions Locked'}
                                    </button>
                                )}

                                <button
                                    onClick={cancelExecution}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all"
                                >
                                    <X size={12} /> Cancel
                                </button>
                            </div>

                            <button
                                onClick={approveAndExecute}
                                disabled={preview.missingConnectors.length > 0 || isExecuting}
                                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white transition-all disabled:opacity-40 shadow-lg shadow-emerald-500/20"
                            >
                                {isExecuting ? (
                                    <><Loader2 size={14} className="animate-spin" /> Executing...</>
                                ) : (
                                    <><Play size={14} /> {dryRun ? 'Dry Run' : 'Execute'}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════ */}
            {/* PHASE: EXECUTING                            */}
            {/* ═══════════════════════════════════════════ */}

            {phase === 'executing' && (
                <div className="glass p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-hig-accent/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Loader2 size={28} className="text-hig-accent animate-spin" />
                    </div>
                    <h3 className="text-lg font-semibold text-hig-text-primary mb-2">Executing governed pipeline...</h3>
                    <p className="text-sm text-hig-text-secondary mb-4">
                        Your Replica is executing each step with full governance checks
                    </p>
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-[10px] bg-hig-accent/10 text-hig-accent px-2 py-1 rounded-full animate-pulse">
                            Write Barrier Active
                        </span>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full">
                            Draft-First for Irreversible
                        </span>
                        <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-1 rounded-full">
                            Idempotency Protected
                        </span>
                    </div>
                    <button
                        onClick={cancelExecution}
                        className="mt-6 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all mx-auto"
                    >
                        <PauseCircle size={12} /> Kill-Switch
                    </button>
                </div>
            )}

            {/* ═══════════════════════════════════════════ */}
            {/* PHASE: VERIFICATION                         */}
            {/* ═══════════════════════════════════════════ */}

            {phase === 'verify' && verification && (() => {
                // Categorize steps
                const completedSteps = verification.steps.filter((s: any) => s.status === 'SUCCESS');
                const blockedSteps = verification.steps.filter((s: any) => s.status === 'BLOCKED' || s.status === 'SKIPPED');
                const failedSteps = verification.steps.filter((s: any) => s.status === 'FAILED');
                const allFailed = completedSteps.length === 0 && failedSteps.length > 0;
                const hasBlocked = blockedSteps.length > 0;

                // Extract step outputs by type
                const ragStep = verification.steps.find((s: any) => s.action === 'rag.gather' && s.status === 'SUCCESS');
                const summaryStep = verification.steps.find((s: any) => s.action === 'llm.summarize' && s.status === 'SUCCESS');
                const extractStep = verification.steps.find((s: any) => s.action === 'llm.extract' && s.status === 'SUCCESS');
                const draftStep = verification.steps.find((s: any) => s.action === 'llm.generate_draft' && s.status === 'SUCCESS');
                const searchStep = verification.steps.find((s: any) => s.action === 'gmail.search' && s.status === 'SUCCESS');
                const docSteps = verification.steps.filter((s: any) => s.action?.startsWith('document.'));

                const mainContent = summaryStep?.artifactContent || extractStep?.artifactContent
                    || summaryStep?.output?.content || extractStep?.output?.content;
                const draftContent = draftStep?.artifactContent || draftStep?.output?.content;
                const ragContext = ragStep?.output?.compressedContext || ragStep?.artifactContent;
                const ragSources = ragStep?.output?.sources;

                return (
                    <div className="space-y-4">
                        {/* ── Smart Status Header ── */}
                        <div className="glass p-5">
                            <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                    allFailed ? 'bg-red-500/10'
                                    : hasBlocked ? 'bg-amber-500/10'
                                    : 'bg-emerald-500/10'
                                }`}>
                                    {allFailed ? <XCircle size={20} className="text-red-400" />
                                     : hasBlocked ? <AlertTriangle size={20} className="text-amber-400" />
                                     : <CheckCircle size={20} className="text-emerald-400" />}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-base font-semibold text-hig-text-primary">
                                        {allFailed ? 'Execution Failed'
                                         : hasBlocked
                                           ? `${completedSteps.length} step${completedSteps.length !== 1 ? 's' : ''} completed, ${blockedSteps.length} awaiting approval`
                                         : `All ${completedSteps.length} steps completed successfully`}
                                    </h3>
                                    <p className="text-xs text-hig-text-secondary mt-0.5">"{verification.objective}"</p>
                                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                                        {completedSteps.length > 0 && (
                                            <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                                                ✓ {completedSteps.length} completed
                                            </span>
                                        )}
                                        {blockedSteps.length > 0 && (
                                            <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full font-medium">
                                                ⏸ {blockedSteps.length} blocked
                                            </span>
                                        )}
                                        {failedSteps.length > 0 && (
                                            <span className="text-[10px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full font-medium">
                                                ✗ {failedSteps.length} failed
                                            </span>
                                        )}
                                        {verification.totalTimeSaved > 0 && (
                                            <span className="text-[10px] bg-hig-accent/10 text-hig-accent px-2 py-0.5 rounded-full flex items-center gap-0.5 font-medium">
                                                <Timer size={8} /> {formatTime(verification.totalTimeSaved)} saved
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Blocked Steps (Write Barrier Explanation) ── */}
                        {hasBlocked && (
                            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                                <div className="flex items-start gap-2">
                                    <Lock size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-amber-400">Write Actions Need Approval</p>
                                        <p className="text-xs text-amber-400/70 mt-1">
                                            {blockedSteps.length} step{blockedSteps.length > 1 ? 's were' : ' was'} blocked by the Write Barrier.
                                            To execute these, re-run with "Write Actions Approved" enabled.
                                        </p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {blockedSteps.map((s: any) => (
                                                <span key={s.stepIndex} className="text-[10px] px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                    {s.action}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── RAG Research Context Card ── */}
                        {ragStep && (
                            <div className="glass overflow-hidden">
                                <div className="p-4 border-b border-hig-border">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                                <Search size={14} className="text-purple-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-hig-text-primary">Research Context</h3>
                                                <p className="text-[10px] text-hig-text-tertiary">Gathered by Agentic RAG</p>
                                            </div>
                                        </div>
                                        {ragSources && (
                                            <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full">
                                                {Array.isArray(ragSources) ? ragSources.length : 0} sources
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {ragContext && (
                                    <div className="p-4">
                                        <div className="text-[13px] text-hig-text-secondary leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto bg-hig-bg-secondary/30 rounded-lg p-3 border border-hig-border/30">
                                            {typeof ragContext === 'string' ? ragContext.slice(0, 1500) : JSON.stringify(ragContext, null, 2).slice(0, 1500)}
                                            {typeof ragContext === 'string' && ragContext.length > 1500 && (
                                                <span className="text-purple-400 text-[11px]"> ...({ragContext.length} total chars)</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!ragContext && ragStep.output && (
                                    <div className="p-4">
                                        <div className="text-[13px] text-hig-text-secondary leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto bg-hig-bg-secondary/30 rounded-lg p-3 border border-hig-border/30">
                                            {JSON.stringify(ragStep.output, null, 2).slice(0, 1500)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Summary / Analysis Card ── */}
                        {mainContent && (
                            <div className="glass overflow-hidden">
                                <div className="p-4 border-b border-hig-border">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-lg bg-hig-accent/10 flex items-center justify-center">
                                            <FileText size={14} className="text-hig-accent" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-hig-text-primary">
                                            {summaryStep ? 'Email Summary' : 'Analysis Results'}
                                        </h3>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="prose prose-sm prose-invert max-w-none text-[13px] text-hig-text-secondary leading-relaxed whitespace-pre-wrap">
                                        {mainContent}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Document Artifacts Card ── */}
                        {docSteps.length > 0 && (
                            <div className="glass overflow-hidden">
                                <div className="p-4 border-b border-hig-border">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                            <FileText size={14} className="text-emerald-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-hig-text-primary">Generated Documents</h3>
                                    </div>
                                </div>
                                <div className="p-4 space-y-3">
                                    {docSteps.map((step: any) => {
                                        const statusStyle = STEP_STATUS_STYLES[step.status] || STEP_STATUS_STYLES.BLOCKED;
                                        const fileType = step.action.split('.')[1]?.toUpperCase() || 'FILE';
                                        const isSuccess = step.status === 'SUCCESS';
                                        const isBlocked = step.status === 'BLOCKED';

                                        return (
                                            <div key={step.stepIndex} className={`p-3 rounded-lg border ${
                                                isSuccess ? 'bg-emerald-500/5 border-emerald-500/20'
                                                : isBlocked ? 'bg-amber-500/5 border-amber-500/20'
                                                : 'bg-red-500/5 border-red-500/20'
                                            }`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg ${isSuccess ? 'bg-emerald-500/10' : isBlocked ? 'bg-amber-500/10' : 'bg-red-500/10'} flex items-center justify-center`}>
                                                            <FileText size={14} className={isSuccess ? 'text-emerald-400' : isBlocked ? 'text-amber-400' : 'text-red-400'} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-hig-text-primary font-medium">
                                                                {step.artifactTitle || `${verification.objective}.${fileType.toLowerCase()}`}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${statusStyle.color} bg-current/10 font-medium`} style={{backgroundColor: 'transparent'}}>
                                                                    <span className={statusStyle.color}>{fileType} • {statusStyle.label}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {isSuccess && step.artifactContent && (
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        const token = localStorage.getItem('accessToken')
                                                                            || (await import('../lib/firebase').then(m => m.auth.currentUser?.getIdToken()));
                                                                        const resp = await fetch(
                                                                            `${getApiUrl()}/actions/${verification.actionRequestId}/artifact/${step.stepIndex}/download`,
                                                                            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                                                        );
                                                                        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                                                                        const blob = await resp.blob();
                                                                        const url = URL.createObjectURL(blob);
                                                                        const a = document.createElement('a');
                                                                        a.href = url;
                                                                        a.download = step.output?.fileName || step.artifactTitle || 'download';
                                                                        document.body.appendChild(a);
                                                                        a.click();
                                                                        a.remove();
                                                                        URL.revokeObjectURL(url);
                                                                    } catch (err) {
                                                                        console.error('Download failed:', err);
                                                                    }
                                                                }}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all cursor-pointer"
                                                            >
                                                                <Download size={12} /> Download
                                                            </button>
                                                        )}
                                                        {isBlocked && (
                                                            <span className="flex items-center gap-1 text-[10px] text-amber-400">
                                                                <Lock size={10} /> Needs write approval
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {isBlocked && (
                                                    <p className="text-[11px] text-amber-400/70 mt-2 ml-11">
                                                        This document was not generated because write actions were not approved.
                                                        Re-run with the write lock unlocked to generate it.
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ── Draft Reply Card ── */}
                        {draftContent && (
                            <div className="glass overflow-hidden border border-hig-accent/20">
                                <div className="p-4 border-b border-hig-border bg-hig-accent/5">
                                    <div className="flex items-center gap-2">
                                        <Edit3 size={14} className="text-hig-accent" />
                                        <h3 className="text-sm font-semibold text-hig-text-primary">Draft Reply</h3>
                                        <span className="text-[9px] bg-hig-accent/10 text-hig-accent px-1.5 py-0.5 rounded-full ml-auto">
                                            Ready to review
                                        </span>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="text-[13px] text-hig-text-secondary leading-relaxed whitespace-pre-wrap bg-hig-bg-secondary/50 rounded-lg p-3 border border-hig-border/50">
                                        {draftContent}
                                    </div>
                                    <div className="flex items-center gap-2 mt-3">
                                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all">
                                            <Send size={10} /> Send Draft
                                        </button>
                                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-hig-accent/10 text-hig-accent hover:bg-hig-accent/20 transition-all">
                                            <Edit3 size={10} /> Edit
                                        </button>
                                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-tertiary hover:text-red-400 transition-all">
                                            <X size={10} /> Discard
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Email Search Results (fallback if no summary) ── */}
                        {!mainContent && searchStep && (() => {
                            try {
                                const parsed = JSON.parse(searchStep.stepVerification);
                                if (parsed.type === 'email_search' && parsed.results) {
                                    return (
                                        <div className="glass overflow-hidden">
                                            <div className="p-4 border-b border-hig-border">
                                                <div className="flex items-center gap-2">
                                                    <Send size={14} className="text-hig-accent" />
                                                    <h3 className="text-sm font-semibold text-hig-text-primary">
                                                        {parsed.resultCount} Emails Found
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                                                {parsed.results.slice(0, 10).map((email: any, i: number) => (
                                                    <div key={i} className="p-3 rounded-lg bg-hig-bg-secondary/50 border border-hig-border/30">
                                                        <p className="text-xs font-medium text-hig-text-primary">{email.subject || '(no subject)'}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-[10px] text-hig-text-secondary">{email.from?.split('<')[0]?.trim()}</span>
                                                            <span className="text-[9px] text-hig-text-tertiary">{email.date ? new Date(email.date).toLocaleDateString() : ''}</span>
                                                        </div>
                                                        {email.snippet && <p className="text-[11px] text-hig-text-tertiary mt-1 line-clamp-2">{email.snippet}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                            } catch { /* not JSON */ }
                            return null;
                        })()}

                        {/* ── Errors ── */}
                        {failedSteps.length > 0 && (
                            <div className="glass overflow-hidden border border-red-500/20">
                                <div className="p-3 border-b border-hig-border bg-red-500/5">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle size={14} className="text-red-400" />
                                        <h3 className="text-xs font-semibold text-red-400">{failedSteps.length} step{failedSteps.length > 1 ? 's' : ''} failed</h3>
                                    </div>
                                </div>
                                <div className="p-3 space-y-1.5">
                                    {failedSteps.map((step: any) => (
                                        <div key={step.stepIndex} className="flex items-start gap-2">
                                            <XCircle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-[11px] text-hig-text-primary font-medium">{step.action}</p>
                                                <p className="text-[10px] text-red-400/70">{step.stepVerification?.slice(0, 300)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Step Timeline (always visible) ── */}
                        <div className="glass overflow-hidden">
                            <div className="p-4 border-b border-hig-border">
                                <h3 className="text-xs font-semibold text-hig-text-secondary uppercase tracking-wider flex items-center gap-2">
                                    <Layers size={12} className="text-hig-text-tertiary" />
                                    Execution Pipeline ({verification.totalSteps} steps)
                                </h3>
                            </div>
                            <div className="p-3 space-y-1">
                                {verification.steps.map((step: any, idx: number) => {
                                    const statusStyle = STEP_STATUS_STYLES[step.status] || STEP_STATUS_STYLES.BLOCKED;
                                    const StatusIcon = statusStyle.Icon;
                                    const ToolIcon = TOOL_ICONS[step.action?.split('.')[0]] || Zap;
                                    const hasOutput = step.output || step.artifactContent;

                                    return (
                                        <div key={step.stepIndex} className="flex items-center gap-3 p-2 rounded-lg hover:bg-hig-bg-tertiary/50 transition-all">
                                            {/* Step number */}
                                            <div className="w-5 h-5 rounded-full bg-hig-bg-secondary flex items-center justify-center text-[9px] text-hig-text-tertiary font-medium flex-shrink-0">
                                                {idx + 1}
                                            </div>
                                            {/* Status icon */}
                                            <StatusIcon size={14} className={statusStyle.color} />
                                            {/* Action name */}
                                            <span className="text-[12px] text-hig-text-primary font-medium">{step.action}</span>
                                            {/* Status label */}
                                            <span className={`text-[10px] ${statusStyle.color} ml-1`}>{statusStyle.label}</span>
                                            {/* Output preview */}
                                            {hasOutput && (
                                                <span className="text-[9px] text-hig-text-tertiary ml-auto truncate max-w-[200px]">
                                                    {step.output?.content?.slice(0, 60) || step.output?.resultCount + ' results' || '✓'}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Action Buttons ── */}
                        <div className="glass p-4">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => processVerification('REJECT_ALL')}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                >
                                    <XCircle size={12} /> Dismiss
                                </button>
                                <div className="flex items-center gap-2">
                                    {hasBlocked && (
                                        <button
                                            onClick={async () => {
                                                if (!preview) return;
                                                setWriteApproved(true);
                                                setIsExecuting(true);
                                                setPhase('executing');
                                                try {
                                                    // Create a rerun action request
                                                    const rerunRes = await api.post<{ newActionRequestId: string }>(`/actions/${preview.actionRequestId}/rerun`);
                                                    const newId = rerunRes.data?.newActionRequestId;
                                                    if (!newId) throw new Error('Rerun failed — no new ID');
                                                    // Execute with writes approved
                                                    await api.post(`/actions/${newId}/approve`, { writeApproved: true });
                                                    // Load results
                                                    const verifyRes = await api.get<VerificationSummary>(`/actions/${newId}/verify`);
                                                    if (verifyRes.data) {
                                                        setVerification(verifyRes.data);
                                                        setPreview({ ...preview, actionRequestId: newId });
                                                        setPhase('verify');
                                                    }
                                                } catch (err: any) {
                                                    console.error('Re-run failed:', err);
                                                    setPhase('verify'); // Stay on verify so user sees the error
                                                } finally {
                                                    setIsExecuting(false);
                                                }
                                            }}
                                            disabled={isExecuting}
                                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all border border-amber-500/20 disabled:opacity-40"
                                        >
                                            {isExecuting ? (
                                                <><Loader2 size={12} className="animate-spin" /> Re-running...</>
                                            ) : (
                                                <><Unlock size={12} /> Re-run with Writes</>
                                            )}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => processVerification('ACCEPT_ALL')}
                                        className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white transition-all shadow-lg shadow-emerald-500/20"
                                    >
                                        <CheckCircle size={14} /> Done
                                    </button>
                                    <button
                                        onClick={resetAll}
                                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all border border-hig-border"
                                    >
                                        <RefreshCw size={12} /> New Objective
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Governance Footer ── */}
            <div className="flex items-start gap-2 p-4 rounded-lg bg-hig-bg-tertiary border border-hig-border mt-6">
                <Shield size={16} className="text-hig-text-tertiary flex-shrink-0 mt-0.5" />
                <p className="text-xs text-hig-text-secondary">
                    All actions are protected by the <strong className="text-hig-text-primary">Write Barrier</strong>,{' '}
                    <strong className="text-hig-text-primary">Draft-First</strong> for irreversible actions, and{' '}
                    <strong className="text-hig-text-primary">idempotency locks</strong>.
                    Every step is recorded in the immutable Work Ledger.
                </p>
            </div>
        </div>
    );
}
